import { useState } from 'react'
import { useToast } from '@features/toast/hooks'
import {
  useAuthGetFollowersQuery,
  useAuthGetUserGoalsQuery,
  useAuthUpdateProfileMutation,
  useAuthWhoamiQuery,
} from '@app/services/auto-generated/auth'
import {
  Box,
  Button,
  Divider,
  Grid,
  Modal,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@common/components'
import { SubmitHandler, useForm } from '@lib/form'
import 'cropperjs/dist/cropper.css'
import { GoalsForm } from '@features/panels/components/video-call/user-details-modal/user-details-modal'
import { authApi } from '@app/services'
import { useAppDispatch, useToggle } from '@common/hooks'
import { FavoriteBorderIcon } from '@common/icons'
import { handleGravatar } from '@common/utils/gravatar'
import { useLocation } from '@lib/router'
import CollectGoalsData from '../collect-goals-data'
import { ModalUpdateImage } from './modal-update-image'

type AccountForm = {
  full_name: string
  image?: string
}

const UpdateAccountForm = (props: { displayFollowers?: boolean }) => {
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const { pathname } = useLocation()
  const dispatch = useAppDispatch()
  const { data } = useAuthWhoamiQuery()
  const { open, handleOpen, handleClose } = useToggle(false)
  const {
    data: userGoal,
    isLoading: isLoadingGoals,
    refetch,
  } = useAuthGetUserGoalsQuery(
    {
      userId: data?.id as number,
    },
    { refetchOnMountOrArgChange: true },
  )
  const [updateProfile] = useAuthUpdateProfileMutation()
  const {
    reset,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<AccountForm>({
    defaultValues: {
      full_name: '',
      image: '',
    },
    values: {
      full_name: data?.full_name || '',
      image: data?.image || '',
    },
    // resolver: yupResolver(updateAccountSchema),
  })
  const { data: listFollowers, isLoading: isLoadingFollowers } = useAuthGetFollowersQuery(
    {
      userId: data?.id as number,
    },
    { refetchOnMountOrArgChange: true, skip: !props.displayFollowers },
  )
  const onSubmit: SubmitHandler<AccountForm> = async (formState) => {
    try {
      setIsLoading(true)
      // console.log('test', formState)
      dispatch(
        authApi.util.updateQueryData('authWhoami', undefined, (draft) => {
          draft.full_name = formState.full_name
          draft.image = formState.image
        }),
      )
      await updateProfile({
        updateProfileRequest: { full_name: formState.full_name, image: formState.image },
      }).unwrap()
      setIsLoading(false)
      toast('profile updated successfully')
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  const UpdateImage = async (newImage: string) => {
    dispatch(
      authApi.util.updateQueryData('authWhoami', undefined, (draft) => {
        draft.image = newImage
      }),
    )
    setValue('image', newImage)
    await updateProfile({
      updateProfileRequest: { image: newImage },
    }).unwrap()
  }
  return (
    <Box>
      <Stack justifyContent={'center'} alignItems='center' spacing={2}>
        {/* <ImageContainer>
          <Avatar src={handleGravatar(watch('image'))} sx={{ width: '100%', height: '100%' }} />
        </ImageContainer> */}
        <ModalUpdateImage
          avatar={handleGravatar(watch('image'))}
          handleUpload={UpdateImage}
          width='75px'
          height='75px'
        />
        {props.displayFollowers && (
          <Tooltip title='Followers'>
            {isLoadingFollowers ? (
              <Skeleton variant='rectangular' height={30} width={150} />
            ) : (
              <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                <FavoriteBorderIcon fontSize='small' />
                <Typography>{listFollowers?.length}</Typography>
              </Stack>
            )}
          </Tooltip>
        )}
      </Stack>
      <Box component='form' onSubmit={handleSubmit(onSubmit)}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          {pathname.startsWith('/profile') && (
            <>
              <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                <TextField
                  variant='filled'
                  fullWidth
                  value={data?.username || ''}
                  disabled
                  label='Username'
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                <TextField
                  variant='filled'
                  fullWidth
                  value={data?.email || ''}
                  disabled
                  label='Email Address'
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <TextField name='full_name' fullWidth label={'Name'} control={control} />
          </Grid>
        </Grid>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Button disabled={!isDirty} onClick={() => reset({})} variant='outlined' color='neutral'>
            Cancel
          </Button>
          <Button type='submit' disabled={!isDirty} variant='contained' loading={isLoading}>
            Save
          </Button>
        </Stack>
        <Stack spacing={2} mt={2}>
          <Divider />
          <Typography variant='h7'>About Me</Typography>
          {!userGoal?.about_me &&
          userGoal?.occupation?.status === '' &&
          userGoal?.occupation?.description === '' &&
          userGoal?.goal?.length === 0 &&
          userGoal?.interests?.length === 0 ? (
            <>
              <Typography variant='body2' color='text.secondary'>
                Add your goals and interests to help us find the best speaking partner for you.
              </Typography>
              <Button variant='contained' sx={{ alignSelf: 'flex-end' }} onClick={handleOpen}>
                Edit Profile
              </Button>
            </>
          ) : (
            <>
              <GoalsForm isLoading={isLoadingGoals} userGoal={userGoal} />
              <Button variant='contained' sx={{ alignSelf: 'flex-end' }} onClick={handleOpen}>
                Edit Profile
              </Button>
            </>
          )}
          {open && (
            <Modal open={open} onClose={handleClose}>
              <CollectGoalsData
                sx={{ maxHeight: '550px' }}
                onClose={async () => {
                  await refetch()
                  handleClose()
                }}
              />
            </Modal>
          )}
        </Stack>
      </Box>
    </Box>
  )
}

export default UpdateAccountForm
